import { useGetApiToken } from "@/features/tokens/api/token.queries";
import {
  getMonitoringTypeFromToken,
  MONITORING_TYPES,
  type MonitoringType,
} from "@/constants/monitoring";
import type { IApiKey } from "@/features/tokens/types/token.types";

// Shared query parameters to prevent duplicate API calls
const SHARED_QUERY_PARAMS = { page: 1, pageSize: 100, activeOnly: true };

export const useKeywordApiToken = () => {
  const { data, isPending, isError } = useGetApiToken(
    SHARED_QUERY_PARAMS.page,
    SHARED_QUERY_PARAMS.pageSize,
    SHARED_QUERY_PARAMS.activeOnly,
  );

  // Match based on entity_types array containing "social_media" or "keyword"
  const keywordToken = data?.api_keys?.find(
    (t: IApiKey) =>
      // t.entity_types?.includes("social_media") ||
      // t.entity_types?.includes("keyword") ||
      // Fallback to prefix check for backwards compatibility
      t.api_key_preview.startsWith("enterprise_") ||
      t.api_key_preview.startsWith("km_") ||
      t.api_key_preview.startsWith("premium"),
  );

  return {
    apiToken: keywordToken?.api_key_preview || null,
    selectedPlatforms: keywordToken?.selected_platforms || [],
    isPending,
    isError,
  };
};

// export const usePostSearchApiToken = () => {
//   const { data, isPending, isError } = useGetApiToken(
//     SHARED_QUERY_PARAMS.page,
//     SHARED_QUERY_PARAMS.pageSize,
//     SHARED_QUERY_PARAMS.activeOnly,
//   );

//   // Match based on entity_types array containing "posts" or "post_search"
//   const postSearchToken = data?.api_keys?.find(
//     (t: IApiKey) =>
//       t.entity_types?.includes("posts") ||
//       t.entity_types?.includes("post_search") ||
//       // Fallback to prefix check
//       t.api_key_preview.startsWith("ps_") ||
//       t.api_key_preview.startsWith("premium"),
//   );

//   return {
//     apiToken: postSearchToken?.api_key_preview || null,
//     isPending,
//     isError,
//   };
// };

export const useInfluencersApiToken = () => {
  const { data, isPending, isError } = useGetApiToken(
    SHARED_QUERY_PARAMS.page,
    SHARED_QUERY_PARAMS.pageSize,
    SHARED_QUERY_PARAMS.activeOnly,
  );

  // Match based on entity_types array containing "influencers"
  const influencersToken = data?.api_keys?.find(
    (t: IApiKey) =>
      // t.entity_types?.includes("influencers") ||
      // Fallback to prefix check (must be more specific to avoid matching "inf_enterprise")
      t.api_key_preview.startsWith("inf_") ||
      t.api_key_preview.startsWith("premium"),
  );

  return {
    apiToken: influencersToken?.api_key_preview || null,
    selectedPlatforms: influencersToken?.selected_platforms || [],
    isPending,
    isError,
  };
};

export const useQuadMediaApiToken = () => {
  const { data, isPending, isError } = useGetApiToken(
    SHARED_QUERY_PARAMS.page,
    SHARED_QUERY_PARAMS.pageSize,
    SHARED_QUERY_PARAMS.activeOnly,
  );

  // Match based on entity_types array containing "quadmedia"
  const quadMediaToken = data?.api_keys?.find(
    (t: IApiKey) =>
      // t.entity_types?.includes("quadmedia") ||
      // Fallback to prefix check
      t.api_key_preview.startsWith("sm_") ||
      t.api_key_preview.startsWith("premium"),
  );

  return {
    apiToken: quadMediaToken?.api_key_preview || null,
    isPending,
    isError,
  };
};

export const usePulseApiToken = () => {
  const { data, isPending, isError } = useGetApiToken(
    SHARED_QUERY_PARAMS.page,
    SHARED_QUERY_PARAMS.pageSize,
    SHARED_QUERY_PARAMS.activeOnly,
  );

  // Match based on entity_types array containing "pulse"
  const pulseToken = data?.api_keys?.find(
    (t: IApiKey) =>
      // t.entity_types?.includes("pulse") ||
      // Fallback to prefix check
      t.api_key_preview.startsWith("pulse_") ||
      t.api_key_preview.startsWith("premium"),
  );

  return {
    apiToken: pulseToken?.api_key_preview || null,
    isPending,
    isError,
  };
};

export const useApiTokenCollection = () => {
  return {
    keyword: useKeywordApiToken(),
    // postSearch: usePostSearchApiToken(),
    influencers: useInfluencersApiToken(),
    quadMedia: useQuadMediaApiToken(),
    pulse: usePulseApiToken(),
  };
};

// Enhanced token type with monitoring information
export interface TokenWithMonitoring {
  token: string;
  token_id: string;
  entity_type: string;
  monitoringType: MonitoringType;
  label: string;
  icon: string;
  description: string;
  isPremium: boolean;
  plan_name: string;
  selected_platforms: string[];
}

// Hook to get all available tokens with monitoring type information
export const useAllAvailableTokens = () => {
  const { data, isPending, isError } = useGetApiToken(
    SHARED_QUERY_PARAMS.page,
    SHARED_QUERY_PARAMS.pageSize,
    SHARED_QUERY_PARAMS.activeOnly,
  );

  const tokensWithMonitoring: TokenWithMonitoring[] =
    data?.api_keys?.map((token: IApiKey) => {
      let monitoringType: MonitoringType;
      let label: string;
      let description: string;

      // Determine monitoring type based on entity_types first, then fallback to prefix
      if (token.entity_types?.includes("pulse")) {
        monitoringType = MONITORING_TYPES.PULSE;
        label = "Pulse Monitoring";
        description = "Advanced pulse monitoring and analytics";
      } else if (
        token.entity_types?.includes("social_media") ||
        token.entity_types?.includes("keyword") ||
        token.api_key_preview.startsWith("enterprise_")
      ) {
        monitoringType = MONITORING_TYPES.KEYWORD;
        label = "Social Media / Keyword Monitoring";
        description = "Monitor keywords across social media platforms";
      } else if (
        token.entity_types?.includes("influencers") ||
        token.api_key_preview.startsWith("inf_")
      ) {
        monitoringType = MONITORING_TYPES.INFLUENCER;
        label = "Influencer Monitoring";
        description = "Track influencer activities and engagement";
      } else if (
        token.entity_types?.includes("quadmedia") ||
        token.api_key_preview.startsWith("sm_") ||
        token.api_key_preview.startsWith("quadmedia_")
      ) {
        monitoringType = MONITORING_TYPES.SOURCE;
        label = "Source/QuadMedia Monitoring";
        description = "Monitor content from specific sources";
      }
      // else if (
      //   token.entity_types?.includes("posts") ||
      //   token.entity_types?.includes("post_search") ||
      //   token.api_key_preview.startsWith("ps_")
      // ) {
      //   monitoringType = MONITORING_TYPES.POST_SEARCH;
      //   label = "Post Search";
      //   description = "Search and monitor specific posts";
      // }
      else if (token.api_key_preview.startsWith("premium")) {
        monitoringType = MONITORING_TYPES.KEYWORD;
        label = "Premium Access";
        description = "Premium access to all monitoring features";
      } else {
        // Fallback to determining from token string
        monitoringType = getMonitoringTypeFromToken(token.api_key_preview);
        label = `${monitoringType.charAt(0).toUpperCase() + monitoringType.slice(1)} Monitoring`;
        description = `Monitor ${monitoringType} content`;
      }

      // Determine if it's a premium token
      const isPremium = token.api_key_preview.startsWith("premium");

      // Get icon based on premium status or monitoring type
      let icon: string;
      if (isPremium) {
        icon = "ri-star-line";
      } else {
        switch (monitoringType) {
          case MONITORING_TYPES.KEYWORD:
            icon = "ri-hashtag";
            break;
          case MONITORING_TYPES.INFLUENCER:
            icon = "ri-speak-line";
            break;
          case MONITORING_TYPES.SOURCE:
            icon = "ri-article-line";
            break;
          case MONITORING_TYPES.PULSE:
            icon = "ri-radar-line";
            break;
          // case MONITORING_TYPES.POST_SEARCH:
          //   icon = "ri-search-line";
          //   break;
          default:
            icon = "ri-hashtag";
        }
      }

      return {
        token: token.api_key_preview,
        token_id: token.api_key_id,
        entity_type: token.entity_types[0],
        plan_name: token.plan_name,
        monitoringType,
        label,
        icon,
        description,
        isPremium,
        selected_platforms: token.selected_platforms || [],
      };
    }) || [];

  return {
    tokens: tokensWithMonitoring,
    isPending,
    isError,
  };
};

// Hook to get tokens filtered by monitoring type
export const useTokensByMonitoringType = (monitoringType: MonitoringType) => {
  const { tokens, isPending, isError } = useAllAvailableTokens();

  const filteredTokens = tokens.filter((token) => {
    // Premium tokens are available for all monitoring types
    if (token.isPremium) return true;

    // Filter by specific monitoring type
    return token.monitoringType === monitoringType;
  });

  return {
    tokens: filteredTokens,
    isPending,
    isError,
  };
};

// Specific hooks for each monitoring type
export const useKeywordTokens = () =>
  useTokensByMonitoringType(MONITORING_TYPES.KEYWORD);
export const useInfluencerTokens = () =>
  useTokensByMonitoringType(MONITORING_TYPES.INFLUENCER);
export const useSourceTokens = () =>
  useTokensByMonitoringType(MONITORING_TYPES.SOURCE);
export const usePulseTokens = () =>
  useTokensByMonitoringType(MONITORING_TYPES.PULSE);
// export const usePostSearchTokens = () =>
//   useTokensByMonitoringType(MONITORING_TYPES.POST_SEARCH);
